import { withAuthRecovery } from '@/lib/authRecovery'
import { getSupabase } from '@/lib/supabase'

export type CrmAccountTransferStatus =
  | 'detected'
  | 'needs_crm_action'
  | 'needs_confirmation'
  | 'verified'
  | 'closed'
  | 'postponed'
  | 'dismissed'

export type CrmAccountTransferModel = 'new_account' | 'same_account_rename'

export type CrmAccountTransferCase = {
  id: number
  status: CrmAccountTransferStatus
  detection_kind: string
  transfer_model: CrmAccountTransferModel | null
  studio_internal_id: number
  studio_number: number
  city_name: string | null
  address: string | null
  transition_date: string
  old_account: string | null
  proposed_account: string | null
  old_partner_number: number | null
  old_persons_fio: string
  new_partner_number: number | null
  new_persons_fio: string
  suspicious_studio_number: number | null
  evidence: Record<string, unknown>
  operator_note: string | null
  postponed_until: string | null
  crm_action_confirmed_at: string | null
  applied_at: string | null
  verified_at: string | null
  closed_at: string | null
  dismissed_at: string | null
  created_at: string
  updated_at: string
}

export type CreateCrmAccountTransferCaseInput = {
  studioInternalId: number
  transitionDate: string
  detectionKind?: string
  transferModel?: CrmAccountTransferModel | null
  oldAccount?: string | null
  proposedAccount?: string | null
  oldPartnerNumber?: number | null
  newPartnerNumber?: number | null
  suspiciousStudioNumber?: number | null
  evidence?: Record<string, unknown>
  status?: CrmAccountTransferStatus
}

export type CrmAccountTransferStudioOption = {
  studio_internal_id: number
  studio_number: number
  city_name: string | null
  address: string | null
  status_code: string | null
  current_account: string | null
  current_account_valid_from: string | null
  current_account_source: string | null
  old_account: string | null
  old_partner_number: number | null
  old_persons_fio: string
  new_partner_number: number | null
  new_persons_fio: string
}

export type CrmAccountTransferPartnerOption = {
  partner_number: number
  persons_fio: string
  cities_text: string
}

export type ApplyCrmAccountTransferCaseResult = {
  case_id: number
  studio_internal_id: number
  account: string
  transition_date: string
  closed_studio_links: number
  closed_account_links: number
  legacy_studio_accounts_updated: boolean
  status: CrmAccountTransferStatus
}

// Диапазон пересборки выручки для перехода. Считается на сервере
// (`crm_account_transfer_rebuild_scope`) ОДНОЙ реализацией и для preview, и для
// самого Apply: две реализации разошлись бы, и preview обещал бы не то окно,
// которое соберётся.
export type CrmAccountTransferRebuildScopeAccount = {
  account: string
  currency: string
  rows: number
  amount: number
  foreign_rows: number
  first_date: string
  last_date: string
}

export type CrmAccountTransferRebuildScope = {
  case_id: number
  case_status: CrmAccountTransferStatus
  applied_at: string | null
  studio_internal_id: number
  studio_number: number | null
  transition_date: string
  old_account: string | null
  new_account: string | null
  business_date: string
  // Ниже этой даты агрегат пересобрать нечем: там лежит легаси-выручка,
  // которой нет в реестре платежей.
  ledger_floor: string
  items_first_date: string | null
  items_last_date: string | null
  affected_rows: number
  by_account: CrmAccountTransferRebuildScopeAccount[]
  requested_start: string
  window_start: string
  window_end: string
  window_days: number
  uncovered_before: string | null
  rolling35_start: string
  // Сколько дней окна прежнее правило (current_date - 34) не покрывало.
  days_before_rolling35: number
  has_window: boolean
}

export type RebuildCrmAccountTransferCaseWindowResult = {
  case_id: number
  window_start: string
  window_end: string
  scope: CrmAccountTransferRebuildScope
  refresh: {
    changed_count: number
    rebuilt_dates_count: number
    foreign_count: number
    foreign_amount: number
  }
}

export type CrmPaymentComparisonStatus =
  | 'missing_accounts'
  | 'same_account'
  | 'no_payments'
  | 'exact_duplicate'
  | 'same_total_different_payments'
  | 'partial_overlap'
  | 'only_old'
  | 'only_new'
  | 'different_totals'

// Какой из наборов доехал до учтённой выручки (`report_payments`).
// `booked_both` = задвоение уже случилось, а не «возможно».
export type CrmPaymentBookingState =
  | 'not_booked'
  | 'booked_old'
  | 'booked_new'
  | 'booked_both'
  | 'booked_other'

export type CrmPaymentComparisonTotals = {
  currency: string
  old_count: number
  old_amount: number
  new_count: number
  new_amount: number
  matched_count: number
  old_only_count: number
  new_only_count: number
  amount_delta: number
  status: CrmPaymentComparisonStatus
  booking_state: CrmPaymentBookingState
  ledger_rows: number | null
  ledger_amount: number | null
  foreign_flagged_count: number
  foreign_flagged_amount: number
  // только на уровне валюты: сколько дней задвоено в выручке
  booked_duplicate_days?: number
}

export type CrmPaymentComparisonDay = CrmPaymentComparisonTotals & {
  payment_date: string
}

export type CrmPaymentComparisonItem = {
  payment_id: string
  studio_number: number | null
  previous_studio_number: number | null
  paid_at: string
  amount: number
  payment_method: string
  added_by: string
}

export type CrmPaymentComparisonPair = {
  payment_date: string
  currency: string
  match_status: 'matched' | 'old_only' | 'new_only'
  old_payment: CrmPaymentComparisonItem | null
  new_payment: CrmPaymentComparisonItem | null
}

export type CrmDetectedStudioNumber = {
  studio_number: number
  payment_count: number
  amount: number
  first_date: string
  last_date: string
}

export type CrmPaymentComparison = {
  case_id: number
  check_id?: number
  studio_number: number
  suspicious_studio_number: number | null
  auto_unknown_numbers?: boolean
  detected_studio_numbers?: CrmDetectedStudioNumber[]
  old_account: string | null
  new_account: string | null
  window_days: 35
  window_start: string
  window_end: string
  generated_at: string
  captured_at?: string
  status: CrmPaymentComparisonStatus
  has_overlap: boolean
  has_booked_duplicate: boolean
  ledger_total: number
  foreign_flagged_total: number
  currencies: CrmPaymentComparisonTotals[]
  daily: CrmPaymentComparisonDay[]
  payments: CrmPaymentComparisonPair[]
}

export async function fetchCrmAccountTransferCases(
  status?: CrmAccountTransferStatus | null
): Promise<CrmAccountTransferCase[]> {
  const { data, error } = (await withAuthRecovery(() =>
    (getSupabase() as any).rpc('list_crm_account_transfer_cases', {
      p_status: status ?? null,
    })
  )) as { data: CrmAccountTransferCase[] | null; error: any }

  if (error) throw error
  return data ?? []
}

export async function fetchCrmAccountTransferStudioOptions(
  transitionDate: string
): Promise<CrmAccountTransferStudioOption[]> {
  const { data, error } = (await withAuthRecovery(() =>
    (getSupabase() as any).rpc('list_crm_account_transfer_studio_options', {
      p_transition_date: transitionDate,
    })
  )) as { data: CrmAccountTransferStudioOption[] | null; error: any }

  if (error) throw error
  return data ?? []
}

export async function fetchCrmAccountTransferPartnerOptions(): Promise<CrmAccountTransferPartnerOption[]> {
  const { data, error } = (await withAuthRecovery(() =>
    (getSupabase() as any).rpc('list_crm_account_transfer_partner_options')
  )) as { data: CrmAccountTransferPartnerOption[] | null; error: any }

  if (error) throw error
  return data ?? []
}

export async function createCrmAccountTransferCase(
  input: CreateCrmAccountTransferCaseInput
): Promise<number> {
  const { data, error } = (await withAuthRecovery(() =>
    (getSupabase() as any).rpc('create_crm_account_transfer_case', {
      p_studio_internal_id: input.studioInternalId,
      p_transition_date: input.transitionDate,
      p_detection_kind: input.detectionKind ?? 'manual',
      p_transfer_model: input.transferModel ?? null,
      p_old_account: input.oldAccount?.trim() || null,
      p_proposed_account: input.proposedAccount?.trim() || null,
      p_old_partner_number: input.oldPartnerNumber ?? null,
      p_new_partner_number: input.newPartnerNumber ?? null,
      p_suspicious_studio_number: input.suspiciousStudioNumber ?? null,
      p_evidence: input.evidence ?? {},
      p_status: input.status ?? 'detected',
    })
  )) as { data: number | null; error: any }

  if (error) throw error
  if (data == null) throw new Error('CRM account transfer case was not created')
  return data
}

export async function applyCrmAccountTransferCase(params: {
  caseId: number
  confirmCrmAction?: boolean
  note?: string | null
}): Promise<ApplyCrmAccountTransferCaseResult> {
  const { data, error } = (await withAuthRecovery(() =>
    (getSupabase() as any).rpc('apply_crm_account_transfer_case', {
      p_case_id: params.caseId,
      p_confirm_crm_action: params.confirmCrmAction ?? false,
      p_note: params.note?.trim() || null,
    })
  )) as { data: ApplyCrmAccountTransferCaseResult | null; error: any }

  if (error) throw error
  if (!data) throw new Error('CRM account transfer case was not applied')
  return data
}

export async function fetchCrmAccountTransferRebuildScope(
  caseId: number
): Promise<CrmAccountTransferRebuildScope> {
  const { data, error } = (await withAuthRecovery(() =>
    (getSupabase() as any).rpc('get_crm_account_transfer_case_rebuild_scope', {
      p_case_id: caseId,
    })
  )) as { data: CrmAccountTransferRebuildScope | null; error: any }

  if (error) throw error
  if (!data) throw new Error('Диапазон пересборки не рассчитан')
  return data
}

// Триггер пересборки срабатывает только на переходе applied_at из NULL, поэтому
// кейсы, проведённые прежним узким окном, чинятся этой кнопкой, а не ручной
// записью на PROD.
export async function rebuildCrmAccountTransferCaseWindow(
  caseId: number
): Promise<RebuildCrmAccountTransferCaseWindowResult> {
  const { data, error } = (await withAuthRecovery(() =>
    (getSupabase() as any).rpc('rebuild_crm_account_transfer_case_window', {
      p_case_id: caseId,
    })
  )) as { data: RebuildCrmAccountTransferCaseWindowResult | null; error: any }

  if (error) throw error
  if (!data) throw new Error('Выручка не пересобрана')
  return data
}

export type CrmAccountTransferAction =
  | 'postpone'
  | 'dismiss'
  | 'mark_verified'
  | 'close'

export type UpdateCrmAccountTransferCaseStatusResult = {
  case_id: number
  action: CrmAccountTransferAction
  previous_status: CrmAccountTransferStatus
  status: CrmAccountTransferStatus
  postponed_until: string | null
  verified_at: string | null
  open: boolean
}

export async function updateCrmAccountTransferCaseStatus(params: {
  caseId: number
  action: CrmAccountTransferAction
  note?: string | null
  postponedUntil?: string | null
}): Promise<UpdateCrmAccountTransferCaseStatusResult> {
  const { data, error } = (await withAuthRecovery(() =>
    (getSupabase() as any).rpc('update_crm_account_transfer_case_status', {
      p_case_id: params.caseId,
      p_action: params.action,
      p_note: params.note?.trim() || null,
      p_postponed_until: params.postponedUntil ?? null,
    })
  )) as { data: UpdateCrmAccountTransferCaseStatusResult | null; error: any }

  if (error) throw error
  if (!data) throw new Error('Статус кейса не изменён')
  return data
}

export async function fetchLatestCrmPaymentComparison(
  caseId: number
): Promise<CrmPaymentComparison | null> {
  const { data, error } = (await withAuthRecovery(() =>
    (getSupabase() as any).rpc('get_latest_crm_account_transfer_payment_check', {
      p_case_id: caseId,
    })
  )) as { data: CrmPaymentComparison | null; error: any }

  if (error) throw error
  return data ?? null
}

export async function captureCrmPaymentComparison(
  caseId: number
): Promise<CrmPaymentComparison> {
  const { data, error } = (await withAuthRecovery(() =>
    (getSupabase() as any).rpc('capture_crm_account_transfer_payment_comparison', {
      p_case_id: caseId,
    })
  )) as { data: CrmPaymentComparison | null; error: any }

  if (error) throw error
  if (!data) throw new Error('Сверка платежей не сформирована')
  return data
}
